/**
 * SliceSlots - Collection of SliceSlot objects
 *
 * Manages a collection of slots for a single SlicePlane, with methods to
 * validate and remove invalid slots (intersecting, non-piercing, trapped).
 *
 * Ported from cc.sketchchair.geometry.SliceSlots.java
 */

import { SliceSlot } from './SliceSlot.js';

export class SliceSlots {
    /**
     * Create a new SliceSlots collection
     */
    constructor() {
        /**
         * Array of SliceSlot objects
         * @type {Array<SliceSlot>}
         */
        this.slots = [];
    }

    /**
     * Add a slot to the collection
     * @param {SliceSlot} slot - Slot to add
     */
    add(slot) {
        if (!(slot instanceof SliceSlot)) {
            console.warn('SliceSlots.add: Not a SliceSlot instance');
            return;
        }
        this.slots.push(slot);
    }

    /**
     * Remove a slot from the collection
     * @param {SliceSlot} slot - Slot to remove
     * @returns {boolean} True if removed
     */
    remove(slot) {
        const index = this.slots.indexOf(slot);
        if (index !== -1) {
            this.slots.splice(index, 1);
            return true;
        }
        return false;
    }

    /**
     * Get slot at index
     * @param {number} index - Index
     * @returns {SliceSlot|null} Slot or null
     */
    get(index) {
        return this.slots[index] || null;
    }

    /**
     * Get number of slots
     * @returns {number} Count
     */
    size() {
        return this.slots.length;
    }

    /**
     * Clear all slots
     */
    clear() {
        this.slots = [];
    }

    /**
     * Get all slots as array
     * @returns {Array<SliceSlot>} Slots array
     */
    getAll() {
        return this.slots;
    }

    /**
     * Remove slots that intersect each other
     *
     * Priority rules:
     * - Slots tied to legs have highest priority (never removed)
     * - When two slots intersect, remove the one without leg priority
     * - If neither has leg priority, remove the later one
     *
     * Algorithm from SliceSlots.java removeIntersecting()
     */
    removeIntersecting() {
        let removedCount = 0;

        for (let i = 0; i < this.slots.length; i++) {
            const slot1 = this.slots[i];

            for (let j = i + 1; j < this.slots.length; j++) {
                const slot2 = this.slots[j];

                // Check if slots collide
                if (slot1.checkCollision(slot2)) {
                    // Leg-tied slots have priority
                    if (slot2.tiedToLeg && !slot1.tiedToLeg) {
                        // Remove slot1
                        this.slots.splice(i, 1);
                        i--;
                        removedCount++;
                        break; // Move to next slot1
                    } else if (slot1.tiedToLeg && !slot2.tiedToLeg) {
                        // Remove slot2
                        this.slots.splice(j, 1);
                        j--;
                        removedCount++;
                    } else {
                        // Neither has priority, remove slot2 (later one)
                        this.slots.splice(j, 1);
                        j--;
                        removedCount++;
                    }
                }
            }
        }

        if (removedCount > 0) {
            console.log(`SliceSlots: Removed ${removedCount} intersecting slots`);
        }

        return removedCount;
    }

    /**
     * Remove slots that don't pierce through the outline
     *
     * A slot "pierces" if it crosses the edge of the shape outline.
     * Slots that are entirely inside or outside are removed.
     *
     * Exception: Slots tied to legs are always kept.
     *
     * @param {SketchPath} outlinePath - Outline path to check against
     * @returns {number} Number of slots removed
     */
    removeNonPiercing(outlinePath) {
        if (!outlinePath) {
            console.warn('SliceSlots.removeNonPiercing: No outline path provided');
            return 0;
        }

        let removedCount = 0;

        for (let i = this.slots.length - 1; i >= 0; i--) {
            const slot = this.slots[i];

            // Exception: Keep leg-tied slots
            if (slot.tiedToLeg) {
                continue;
            }

            // Check if slot pierces the outline
            if (!this.isPiercing(slot, outlinePath)) {
                this.slots.splice(i, 1);
                removedCount++;
            }
        }

        if (removedCount > 0) {
            console.log(`SliceSlots: Removed ${removedCount} non-piercing slots`);
        }

        return removedCount;
    }

    /**
     * Check if a slot pierces through the outline
     *
     * A slot pierces if at least one of its edges crosses the outline.
     * This is determined by counting intersections - exactly 2 means piercing.
     *
     * @param {SliceSlot} slot - Slot to check
     * @param {SketchPath} outlinePath - Outline to check against
     * @returns {boolean} True if slot pierces outline
     * @private
     */
    isPiercing(slot, outlinePath) {
        const slotOutlines = slot.getAllOutlines();

        for (const outline of slotOutlines) {
            const intersectionCount = this.countIntersections(outline, outlinePath);

            // A piercing slot should intersect the outline at exactly 2 points
            // (entry and exit)
            if (intersectionCount === 2) {
                return true;
            }
        }

        return false;
    }

    /**
     * Remove slots that are trapped inside the shape
     *
     * A slot is "trapped" if it intersects the outline more than 2 times,
     * meaning it's entirely contained within the shape with no way to
     * be cut during fabrication.
     *
     * @param {SketchPath} outlinePath - Outline path to check against
     * @returns {number} Number of slots removed
     */
    removeTrappedSlots(outlinePath) {
        if (!outlinePath) {
            console.warn('SliceSlots.removeTrappedSlots: No outline path provided');
            return 0;
        }

        let removedCount = 0;

        for (let i = this.slots.length - 1; i >= 0; i--) {
            const slot = this.slots[i];

            // Get all outlines for this slot
            const slotOutlines = slot.getAllOutlines();

            for (const outline of slotOutlines) {
                const intersectionCount = this.countIntersections(outline, outlinePath);

                // More than 2 intersections means trapped inside
                if (intersectionCount > 2) {
                    this.slots.splice(i, 1);
                    removedCount++;
                    break; // Move to next slot
                }
            }
        }

        if (removedCount > 0) {
            console.log(`SliceSlots: Removed ${removedCount} trapped slots`);
        }

        return removedCount;
    }

    /**
     * Count intersections between slot outline and path outline
     *
     * @param {Array<Vec2D>} slotOutline - Slot corner points
     * @param {SketchPath} pathOutline - Path to check against
     * @returns {number} Number of intersections
     * @private
     */
    countIntersections(slotOutline, pathOutline) {
        if (!pathOutline || !pathOutline.points || pathOutline.points.length < 2) {
            return 0;
        }

        let count = 0;

        // Check each edge of the slot outline
        for (let i = 0; i < slotOutline.length; i++) {
            const p1 = slotOutline[i];
            const p2 = slotOutline[(i + 1) % slotOutline.length];

            // Check against each edge of the path outline
            for (let j = 0; j < pathOutline.points.length; j++) {
                const p3 = pathOutline.points[j];
                const p4 = pathOutline.points[(j + 1) % pathOutline.points.length];

                if (this.lineSegmentsIntersect(p1, p2, p3, p4)) {
                    count++;
                }
            }
        }

        return count;
    }

    /**
     * Check if two line segments intersect
     *
     * Uses the cross product method to determine if line segments p1-p2
     * and p3-p4 intersect.
     *
     * @param {Vec2D} p1 - First point of first segment
     * @param {Vec2D} p2 - Second point of first segment
     * @param {Vec2D} p3 - First point of second segment
     * @param {Vec2D} p4 - Second point of second segment
     * @returns {boolean} True if segments intersect
     * @private
     */
    lineSegmentsIntersect(p1, p2, p3, p4) {
        const d1 = this.direction(p3, p4, p1);
        const d2 = this.direction(p3, p4, p2);
        const d3 = this.direction(p1, p2, p3);
        const d4 = this.direction(p1, p2, p4);

        if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
            ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
            return true;
        }

        // Check for collinear cases
        if (d1 === 0 && this.onSegment(p3, p4, p1)) return true;
        if (d2 === 0 && this.onSegment(p3, p4, p2)) return true;
        if (d3 === 0 && this.onSegment(p1, p2, p3)) return true;
        if (d4 === 0 && this.onSegment(p1, p2, p4)) return true;

        return false;
    }

    /**
     * Calculate direction (cross product sign)
     * @private
     */
    direction(p1, p2, p3) {
        return (p3.x - p1.x) * (p2.y - p1.y) - (p2.x - p1.x) * (p3.y - p1.y);
    }

    /**
     * Check if point p is on segment p1-p2
     * @private
     */
    onSegment(p1, p2, p) {
        return p.x <= Math.max(p1.x, p2.x) &&
               p.x >= Math.min(p1.x, p2.x) &&
               p.y <= Math.max(p1.y, p2.y) &&
               p.y >= Math.min(p1.y, p2.y);
    }

    /**
     * Cleanup all invalid slots
     *
     * Runs all validation methods in sequence:
     * 1. Remove intersecting slots
     * 2. Remove non-piercing slots
     * 3. Remove trapped slots
     *
     * @param {SketchPath} outlinePath - Outline path for validation
     * @returns {Object} Stats about removed slots
     */
    cleanup(outlinePath) {
        const stats = {
            intersecting: this.removeIntersecting(),
            nonPiercing: this.removeNonPiercing(outlinePath),
            trapped: this.removeTrappedSlots(outlinePath),
            total: 0
        };

        stats.total = stats.intersecting + stats.nonPiercing + stats.trapped;

        if (stats.total > 0) {
            console.log(`SliceSlots cleanup: Removed ${stats.total} invalid slots`);
        }

        return stats;
    }

    /**
     * Clone this collection
     * @returns {SliceSlots} Cloned collection
     */
    clone() {
        const cloned = new SliceSlots();
        this.slots.forEach(slot => {
            cloned.add(slot.clone());
        });
        return cloned;
    }

    /**
     * Export to JSON
     * @returns {Array<Object>} Array of slot JSON
     */
    toJSON() {
        return this.slots.map(slot => slot.toJSON());
    }

    /**
     * Load from JSON
     * @param {Array<Object>} data - Array of slot JSON
     * @param {SlicePlane} plane - Parent plane
     * @returns {SliceSlots} New collection
     */
    static fromJSON(data, plane = null) {
        const collection = new SliceSlots();

        if (Array.isArray(data)) {
            data.forEach(slotData => {
                const slot = SliceSlot.fromJSON(slotData, plane);
                collection.add(slot);
            });
        }

        return collection;
    }

    /**
     * Get string representation
     * @returns {string} Description
     */
    toString() {
        return `SliceSlots[count=${this.slots.length}]`;
    }
}
